import type { CartItem } from '../types';

interface EmailTemplateProps {
  customerDetails: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  items: CartItem[];
  total: number;
  deposit: number;
  type?: 'quote' | 'order';
}

export function EmailTemplate({ customerDetails, items, total, deposit, type = 'quote' }: EmailTemplateProps) {
  const today = new Date().toLocaleDateString('en-GB');
  const quoteValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB');
  const remainingBalance = total - deposit;
  
  const itemRows = items.map(item => {
    const itemTotal = (item.price + item.optionsCost) * item.quantity;
    const optionsBreakdown = Object.entries(item.selectedOptions)
      .map(([name, value]) => {
        const option = item.options.find(opt => opt.name === name);
        const optionValue = option?.values.find(v => v.value === value);
        if (optionValue?.additionalCost) {
          return `${name}: ${value} (+£${optionValue.additionalCost.toFixed(2)})`;
        }
        return `${name}: ${value}`;
      })
      .join('<br>');

    return `
      <tr>
        <td style="padding: 16px 12px; border-bottom: 1px solid #E5E7EB;">
          <strong>${item.name}</strong>
          <div style="font-size: 14px; color: #6B7280; margin-top: 4px;">
            ${optionsBreakdown}
          </div>
          <div style="font-size: 13px; color: #6B7280; margin-top: 4px;">
            Base Price: £${item.price.toFixed(2)}
            ${item.optionsCost > 0 ? `<br>Options Cost: +£${item.optionsCost.toFixed(2)}` : ''}
            <br>Unit Price: £${(item.price + item.optionsCost).toFixed(2)}
          </div>
        </td>
        <td style="padding: 16px 12px; text-align: right; border-bottom: 1px solid #E5E7EB; vertical-align: top;">
          ${item.quantity}
        </td>
        <td style="padding: 16px 12px; text-align: right; border-bottom: 1px solid #E5E7EB; vertical-align: top;">
          £${(item.price + item.optionsCost).toFixed(2)}
        </td>
        <td style="padding: 16px 12px; text-align: right; border-bottom: 1px solid #E5E7EB; vertical-align: top;">
          £${itemTotal.toFixed(2)}
        </td>
      </tr>
    `;
  }).join('');

  const headerTitle = type === 'quote' ? 'Quote Details' : 'Order Confirmation';
  const headerSubtext = type === 'quote' 
    ? `Valid until: ${quoteValidUntil}`
    : `Order Date: ${today}`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${headerTitle}</title>
</head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif">
  <div style="max-width:600px;margin:0 auto;background:#fff">
    <div style="background:#6389af;color:white;padding:32px;text-align:center">
      <img src="https://img.adminteam.work/lecmg.png" alt="LEC Digital Design" style="height:60px;margin-bottom:16px">
      <h1 style="margin:0;font-size:24px">${headerTitle}</h1>
      <p style="margin:8px 0 0">${headerSubtext}</p>
    </div>

    <div style="padding:32px">
      <div style="background:#F3F4F6;border-radius:8px;padding:24px;margin-bottom:32px">
        <h2 style="margin:0 0 16px;color:#6389af">Customer Information</h2>
        <p style="margin:4px 0"><strong>Name:</strong> ${customerDetails.firstName} ${customerDetails.lastName}</p>
        <p style="margin:4px 0"><strong>Email:</strong> ${customerDetails.email}</p>
        <p style="margin:4px 0"><strong>Phone:</strong> ${customerDetails.phone}</p>
        ${type === 'quote' ? `<p style="margin:4px 0"><strong>Quote Date:</strong> ${today}</p>` : ''}
      </div>

      <h2 style="margin:24px 0 16px;color:#6389af">Order Summary</h2>
      <table style="width:100%;border-collapse:collapse">
        <tr style="background:#F3F4F6">
          <th style="padding:12px;text-align:left;border-bottom:1px solid #E5E7EB">Item Details</th>
          <th style="padding:12px;text-align:right;border-bottom:1px solid #E5E7EB">Qty</th>
          <th style="padding:12px;text-align:right;border-bottom:1px solid #E5E7EB">Unit Price</th>
          <th style="padding:12px;text-align:right;border-bottom:1px solid #E5E7EB">Total</th>
        </tr>
        ${itemRows}
      </table>

      <div style="margin-top:32px;border-top:2px solid #E5E7EB;padding-top:16px">
        <div style="display:flex;justify-content:space-between;margin:8px 0">
          <strong>Total Amount:</strong>
          <span>£${total.toFixed(2)}</span>
        </div>
        ${type === 'order' ? `
        <div style="display:flex;justify-content:space-between;margin:8px 0;color:#16a34a;font-size:18px;font-weight:bold">
          <span>Deposit Paid:</span>
          <span>£${deposit.toFixed(2)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;margin:8px 0;color:#6389af;font-size:18px;font-weight:bold">
          <span>Remaining Balance:</span>
          <span>£${remainingBalance.toFixed(2)}</span>
        </div>
        ` : `
        <div style="display:flex;justify-content:space-between;margin:8px 0;color:#6389af;font-size:18px;font-weight:bold">
          <span>Required Deposit (50%):</span>
          <span>£${deposit.toFixed(2)}</span>
        </div>
        `}
      </div>

      <div style="margin-top:32px;padding:24px;background:#F3F4F6;border-radius:8px">
        ${type === 'quote' ? `
        <p style="margin:0 0 8px"><strong>Terms & Conditions:</strong></p>
        <ul style="margin:0;padding-left:20px;font-size:14px;color:#4B5563">
          <li>This quote is valid for 30 days from the date of issue</li>
          <li>A 50% deposit is required to confirm your order</li>
          <li>The remaining balance is due before delivery</li>
          <li>Delivery times may vary based on product availability</li>
        </ul>
        ` : `
        <p style="margin:0 0 8px"><strong>Next Steps:</strong></p>
        <ul style="margin:0;padding-left:20px;font-size:14px;color:#4B5563">
          <li>Your order has been confirmed with the deposit payment</li>
          <li>The remaining balance of £${remainingBalance.toFixed(2)} is due before delivery</li>
          <li>We will contact you shortly to arrange delivery details</li>
          <li>Please keep this email for your records</li>
        </ul>
        `}
      </div>
    </div>

    <div style="background:#F3F4F6;padding:24px;text-align:center;color:#6B7280;font-size:14px">
      <p style="margin:0">Thank you for ${type === 'quote' ? 'your interest in' : 'shopping with'} us!</p>
      <p style="margin:8px 0 0">If you have any questions, please don't hesitate to contact us.</p>
    </div>
  </div>
</body>
</html>`;
}
